/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { redirect, useActionData } from 'react-router';
import type { Route } from './+types/_empty.forgot-password';
import { useTranslation } from 'react-i18next';

// Components
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ForgotPasswordForm } from '@/components/forgot-password-form';

// Lib
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { buildUrlFromContext } from '@/lib/url.server';
import { routes } from '@/route-paths';

// Middleware
import { getAuth, getPasswordResetToken } from '@/middlewares/auth.server';
import { getLogger } from '@/lib/logger.server';
import { enforceTurnstile } from '@/lib/turnstile/enforce.server';

type ForgotPasswordActionData = {
    error?: string;
    success?: boolean;
    email?: string;
};

export function loader({ context }: Route.LoaderArgs): Response | void {
    // If user is already logged in as registered user, redirect to login page
    const session = getAuth(context);
    if (session.userType === 'registered') {
        return redirect(buildUrlFromContext(routes.login, context));
    }
}

// Server action required for authentication - password reset token generation must be handled
// server-side to maintain security and proper integration with SFCC's authentication system
export async function action({ request, context }: Route.ActionArgs): Promise<ForgotPasswordActionData> {
    const logger = getLogger(context);
    const { t } = getTranslation(context);
    const formData = await request.formData();
    const email = formData.get('email')?.toString();
    if (!email) {
        return { error: t('resetPassword:emailRequired') };
    }

    // RGBARRY-CUSTOMIZATION: Turnstile bot protection on the public reset form. enforceTurnstile
    // fail-opens when Turnstile is not configured/enabled, so this never blocks by default. A
    // block is an account-agnostic bot verdict (not "this email exists"), so surfacing it does
    // not leak account existence.
    const turnstileToken = formData.get('turnstileToken')?.toString();
    const allowed = await enforceTurnstile({
        request,
        config: getConfig(context),
        turnstileToken,
        logger,
        actionName: 'forgot-password',
        email,
    });
    if (!allowed) {
        return { error: t('security:turnstile.error') };
    }

    // RGBARRY-CUSTOMIZATION: anti-enumeration — always land on the neutral "check your email"
    // screen regardless of whether the account exists. Failures from the token request
    // (non-existent account, ESP/SLAS errors, etc.) are logged server-side but never surfaced,
    // so the response is byte-for-byte identical for every email and cannot be used to probe
    // which addresses have accounts.
    try {
        // Send password reset token using SLAS and Marketing Cloud (Klaviyo). Existing
        // accounts still receive the reset email; this only changes what the client sees.
        await getPasswordResetToken(context, { email });
        logger.info('ForgotPassword: reset token requested');
    } catch (error) {
        logger.error('ForgotPassword: reset request failed (suppressed from client)', { error });
    }
    return { success: true, email };
}

export default function ForgotPassword(): ReactElement {
    const actionData = useActionData<typeof action>();
    const { t } = useTranslation('resetPassword');

    if (actionData?.success && actionData?.email) {
        return (
            <div data-section="auth" className="flex items-center justify-center bg-background py-12 section-container">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <h2 className="mt-6 text-center text-3xl font-bold text-foreground">{t('checkEmailTitle')}</h2>
                        <p className="mt-2 text-center text-sm text-muted-foreground">
                            {t('checkEmailDescription', { email: actionData.email })}
                        </p>
                    </div>

                    <Card className="p-8">
                        <div className="space-y-6">
                            <Link to={routes.login}>
                                <Button className="w-full cursor-pointer">{t('backToSignIn')}</Button>
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // Initial form state
    return (
        <div data-section="auth" className="flex items-center justify-center bg-background py-12 section-container">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-bold text-foreground">{t('title')}</h2>
                    <p className="mt-2 text-center text-sm text-muted-foreground">{t('subtitle')}</p>
                </div>

                <Card className="p-8">
                    <ForgotPasswordForm error={actionData?.error} />
                </Card>
            </div>
        </div>
    );
}
